import {
  ROUTE_ADDRESS_HOME,
  ROUTE_HOME,
  ROUTE_SEO_ADDRESS,
  ROUTE_SEO_DIRECTORY_CITY_OVM_POIS
} from '../../../../routes'
import { isFranceCountryCode } from '../../../../routes/address/Countries'
import { selectFirstAddress, selectZone } from '../../../../store/address/address.selectors'
import { isCountryRoute, selectCurrentHistoryRoute } from '../../../../store/history/history.selectors'
import { selectLocale } from '../../../../store/locale/locale.selectors'
import { getAppOrFreeAppLabel, selectGeoentityTypeDataForSearch } from '../../../../store/poiassets/poiassets.selectors'
import { searchServiceCtx } from '../../../../store/search/search.service.constants'
import {
  selectSeoAppsForFooter,
  selectSeoCitiesForActivitiesFooter,
  selectSeoCitiesForFooter,
  selectSeoTown
} from '../../../../store/seo/seo.selectors'
import { compact } from '../../../../utils/array'
import { slugify } from '../../../../utils/string'
import { getCountryByLocale, localeCountryIsFrance } from '../../../i18n'
import { translate } from '../../../i18n/T'
import { generateFullRoute } from '../../../router/routeUtils'
import { getPostCodeFromTown, getSeoCityPostcode, getTownForDistrict } from '../../seo'
import { selectSeoCityLinkLabelPrefixKey } from '../seo-footer.selectors'

export const formatCitiesLinks = (links, locale, labelPrefixKey = 'footer.cityLink.label.default') =>
  links
    .map(({ postcode, town, district }) => {
      if (!district) return { postcode, town }
      const townForDistrict = getTownForDistrict({ postcode })
      return { postcode, town: townForDistrict.town, label: town }
    })
    .map(({ town, postcode, label }) => {
      if (!label) label = town
      return { label: translate(locale)(labelPrefixKey, [label]), url: `/plan/${postcode}-${slugify(town)}` }
    })

export const buildCitiesLinks = (store, cityDirectoryLink, countryLink) => {
  const locale = selectLocale(store)
  const labelPrefixKey = selectSeoCityLinkLabelPrefixKey(store)
  const cities = selectSeoCitiesForFooter(store)
  const citiesForLinks = formatCitiesLinks(cities, locale, labelPrefixKey)
  const zone = selectZone(store)
  const route = selectCurrentHistoryRoute(store)

  if (isCountryRoute(route)) {
    const localeIsFr = localeCountryIsFrance(locale)
    const zoneIsFr = isFranceCountryCode(zone?.countryCode)

    if ((localeIsFr && !zoneIsFr) || (!localeIsFr && zoneIsFr)) return {}
  }

  return {
    title: translate(locale)('footer.top-search.cities.title', [zone?.countryLabel ?? getCountryByLocale(locale)]),
    contents: compact([
      cityDirectoryLink ? { label: 'Villes de France', url: '/annuaire-ville/page1' } : null,
      countryLink ? { label: 'Carte de France', url: '/plan/pays/250-france' } : null,
      ...citiesForLinks
    ])
  }
}

export const buildCitiesAppLinks = (store, minimumToDisplay = 0) => {
  const locale = selectLocale(store)
  const { appOrRubricIds, label: appLabel } = selectGeoentityTypeDataForSearch(store)
  const cities = selectSeoCitiesForFooter(store)

  if (cities.length >= minimumToDisplay) {
    const citiesForLinks = cities.map(({ town, label: cityLabel, slug: eventuallySlug }) => ({
      label: `${appLabel} ${cityLabel ?? town}`,
      url: `/app/${appOrRubricIds}/${slugify(eventuallySlug)}`
    }))

    return {
      title: translate(locale)('footer.top-search.cities-app.title'),
      contents: compact(citiesForLinks)
    }
  }
}

export const buildCitiesActivitiesLinks = (store, minimumToDisplay = 0) => {
  const locale = selectLocale(store)
  const { appOrRubricIds, label: appLabel } = selectGeoentityTypeDataForSearch(store)
  const cities = selectSeoCitiesForActivitiesFooter(store)

  if (appOrRubricIds === 'hotel' || cities.length < minimumToDisplay) return {}

  const citiesForLinks = cities.map(({ town, label: cityLabel, slug: eventuallySlug }) => ({
    label: `${appLabel} ${cityLabel ?? town}`,
    url: `/activite/${appOrRubricIds}/${slugify(eventuallySlug)}`
  }))

  return {
    title: translate(locale)('footer.top-search.cities-activity.title', [appLabel]),
    contents: compact(citiesForLinks)
  }
}

export const buildAppsCitiesLinks = (store, minimumToDisplay = 0) => {
  const locale = selectLocale(store)
  const { town, postcode, townWithoutDistrict } = selectSeoTown(store)
  const { postcode: addressPostcode } = selectFirstAddress(store)
  const cityLink = `${postcode ?? addressPostcode} ${townWithoutDistrict ?? town}`
  const apps = selectSeoAppsForFooter(store)

  if (apps.length >= minimumToDisplay) {
    const appsForLinks = apps.map(app => ({
      label: `${getAppOrFreeAppLabel(store, { id: app })} ${town ?? townWithoutDistrict}`,
      url: `/app/${app}/${slugify(cityLink)}`
    }))

    return {
      title: translate(locale)('footer.top-search.app-cities.title', [town ?? townWithoutDistrict]),
      contents: compact(appsForLinks)
    }
  }
}

export const generateBreadcrumbForDirectoryCityFooter = (store, route, translationSuffix, ovm = false) => {
  const { geocode_level, label, town, postcode } = selectFirstAddress(store)
  const sluggedTown = slugify(town)

  const links = [
    { labelKey: 'breadcrumb.homepage', url: generateFullRoute(ROUTE_HOME) },
    { labelKey: 'breadcrumb.pageplan', url: generateFullRoute(ROUTE_ADDRESS_HOME) }
  ]

  if (geocode_level === searchServiceCtx.geocodeLevel.district) {
    const breadcrumbs = [
      ...links,
      {
        labelKey: town,
        url: generateFullRoute(ROUTE_SEO_ADDRESS, { location: `${getSeoCityPostcode(postcode)}-${sluggedTown}` })
      },
      {
        labelKey: label,
        url: generateFullRoute(ROUTE_SEO_ADDRESS, { location: `${postcode}-${sluggedTown}` })
      },
      {
        labelKey: `breadcrumb.directory.city-${translationSuffix}`,
        labelArgs: [town],
        url: generateFullRoute(route, {
          zip: getSeoCityPostcode(postcode),
          city: sluggedTown,
          pageIdx: 1
        })
      },
      {
        labelKey: `breadcrumb.directory.city-${translationSuffix}`,
        labelArgs: [label],
        url: generateFullRoute(route, { zip: postcode, city: sluggedTown, pageIdx: 1 })
      }
    ]
    return ovm
      ? [
          ...breadcrumbs,
          {
            labelKey: `breadcrumb.directory.city-ovm-pois`,
            labelArgs: [town],
            url: generateFullRoute(ROUTE_SEO_DIRECTORY_CITY_OVM_POIS, { zip: postcode, city: sluggedTown, pageIdx: 1 })
          }
        ]
      : breadcrumbs
  } else {
    const townPostCode = getPostCodeFromTown(town) ?? postcode
    const breadcrumbs = [
      ...links,
      {
        labelKey: town,
        url: generateFullRoute(ROUTE_SEO_ADDRESS, { location: `${townPostCode}-${sluggedTown}` })
      },
      {
        labelKey: `breadcrumb.directory.city-${translationSuffix}`,
        labelArgs: [town],
        url: generateFullRoute(route, { zip: townPostCode, city: sluggedTown, pageIdx: 1 })
      }
    ]
    return ovm
      ? [
          ...breadcrumbs,
          {
            labelKey: `breadcrumb.directory.city-ovm-pois`,
            labelArgs: [town],
            url: generateFullRoute(ROUTE_SEO_DIRECTORY_CITY_OVM_POIS, {
              zip: townPostCode,
              city: sluggedTown,
              pageIdx: 1
            })
          }
        ]
      : breadcrumbs
  }
}
